import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'

import type { BillingCard, BillingInvoice, PortalFlow } from '../models/useBilling'
import useBilling, { isFreePlan } from '../models/useBilling'
import { capitalize, formatCents } from '../utilities/general'
import { flushLeft, flushRight } from '../utilities/formAlign'
import { describePaymentMethod } from '../utilities/paymentMethod'
import { balanceLine } from '../utilities/billingBalance'
import { instantUTCFromUnixSeconds } from '../utilities/freshness'
import { freePlanConsequence, summaryVisibility } from '../utilities/subscription'

// The three sections of the billing page that live in Stripe rather than in our database: the card
// on file, the invoice history, and cancellation (app#169).
//
// ⚠⚠ NO CARD IS EVER ENTERED HERE. Every write is a deep link to a Stripe-hosted page
// (router#1134). This component reads and links; it collects nothing.
//
// ⚠ A FREE ORG THAT NEVER SUBSCRIBED SEES NONE OF IT — the parent renders this only for an org
// that has a Stripe customer. Absent, not disabled: such an org has no card, no invoice and
// nothing to cancel, and a greyed-out "Update" invites a click that leads nowhere.
//
// ⚠⚠ A FREE ORG THAT CAME DOWN FROM A PAID PLAN SEES THE CARD AND THE INVOICES (app#265). The card
// stays attached after a cancellation and the portal is the only way to remove it; the invoices
// are the receipts for what was paid. Only CANCELLATION follows the subscription — `hasSubscription`
// is what hides it, because there is nothing left to cancel.
//
// ⚠⚠ AND "NOT FETCHED" IS NOT "EMPTY" (app#216). The summary read is Owner-only, so for an admin
// or a member of a PAID org it never runs — `card` stays null and `invoices` stays empty, and this
// panel printed both as FACTS: "No default payment method on file" and "No invoices yet" to an
// organization that had a Visa on file and two paid invoices. `summaryLoaded` is what separates
// the two, and it is cleared on every org switch (see Billing.tsx), so it never reads stale.
// `CreditBlock` already had this right — `showsCredit` draws no conclusion from a null position —
// and this is that shape.

// ⚠ NOT `{brand} •••• {last4}`. A Stripe Link method has neither, and printing them anyway would
// render " •••• " beside a real monthly charge (router#1171). `describePaymentMethod` owns which
// fields a given method can actually fill.
//
// ⚠⚠ AND THE WALLET IS ALL THERE IS TO PRINT FOR A LINK METHOD (app#207). Naming the card behind
// it was asked for and CANNOT BE DONE BY EITHER REPO: Stripe's `PaymentMethod` of type `link` has
// `card = null` and exposes only `link.email`. Measured 2026-09-03 on the two default methods of
// prod customer `cus_VBkwJHULIPjXim`. A `card` method elsewhere in the same customer's list says
// nothing about the default. So `Link · <email>` is the honest ceiling here, not a stopping short.
function PaymentMethodLine({ card }: Readonly<{ card: BillingCard }>) {
  const { title, detail } = describePaymentMethod(card)
  return (
    <Typography variant="body2">
      {title}
      {detail && (
        <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
          {detail}
        </Typography>
      )}
    </Typography>
  )
}

// The reassurance, which is TRUE, LOAD-BEARING AND STAYS (app#210). It is the first thing an
// anxious customer needs, and it is not the whole answer — `freePlanConsequence` adds what the
// organization lands on. The two sentences differ only in tense: one plan is already set to end.
const KEPT_ON_CANCEL = 'Nothing is deleted when it ends — your devices, files, names and permissions stay.'
const ALREADY_ENDING = `Your plan is set to end at the end of the period you have paid for. ${KEPT_ON_CANCEL}`
const STILL_RUNNING = `You keep the plan until the end of the period you have paid for. ${KEPT_ON_CANCEL}`

// ⚠⚠ IN UTC, WITH THE ZONE NAMED, THROUGH THE SAME FORMATTER AS THE PURCHASE ROWS (app#267). This
// used `toLocaleDateString(undefined, …)`, which is the browser's zone with no zone printed, while
// every other instant on the panel is UTC — so a credit purchase at 00:11 UTC read "Sep 6" one row
// above an invoice created at 00:14 UTC that read "Sep 5". Two events three minutes apart, on two
// days, in front of a customer reconciling a card statement. An invoice is an instant like a
// purchase, so it takes `instantUTC` and not the day-granular `periodDateUTC`. Stripe's stamp is
// Unix seconds; `instantUTCFromUnixSeconds` converts it and returns '' for a non-finite stamp,
// because `new Date(NaN).toISOString()` throws and the app has no ErrorBoundary.
const invoiceDate = (unixSeconds: number): string => instantUTCFromUnixSeconds(unixSeconds)

type Props = {
  card: BillingCard | null
  invoices: BillingInvoice[]
  hasMoreInvoices: boolean
  summaryError: boolean
  cancelAtPeriodEnd: boolean
  // Whether there is a subscription to cancel — the Cancellation section renders only then (app#265).
  hasSubscription: boolean
  canManage: boolean
  busy?: string
  onPortal: (flow?: PortalFlow) => void
  onLoadMore: () => void
}

export default function BillingAccount({
  card,
  invoices,
  hasMoreInvoices,
  summaryError,
  cancelAtPeriodEnd,
  hasSubscription,
  canManage,
  busy,
  onPortal,
  onLoadMore,
}: Readonly<Props>) {
  // ⚠ READ FROM THE STORE RATHER THAN TAKEN AS A PROP, the way `CreditBlock` reads the credit
  // position. The catalog is not this panel's caller's business, and the cancellation sentence
  // needs Free's OWN allowances — the numbers of the plan this organization LANDS ON, not of the
  // plan it is on now (app#210).
  //
  // ⚠ `isFreePlan`, never a plan NAME: it reads `hard_caps`, so a renamed or newly-added
  // hard-capped plan is found here exactly as it is by the banner, Usage and the SSO upsell
  // (app#127). ⚠ The selector returns the state's own array, so the reference is stable — a
  // derived object would loop `getSnapshot` under Zustand v5.
  const plans = useBilling(s => s.plans)
  const landsOn = freePlanConsequence(plans.find(plan => isFreePlan(plan)) ?? null)

  // ⚠⚠ THE ANSWER TO "DID WE ACTUALLY ASK" (app#216), and the ONLY thing that may license an
  // empty state below. From the store rather than through a prop, like `plans` above: the parent
  // already owns the Owner-only gate on the fetch, and threading its consequence through a second
  // prop is how the two get to disagree.
  //
  // ⚠ It is not the same question as `canManage`. THREE states reach this panel — an Owner whose
  // read has landed, an Owner whose read is in flight, and a non-Owner whose read never ran — and
  // only the first may say "no card" or "no invoices". The second says nothing yet; the third says
  // who can see it. `summaryVisibility` is where those three are named and tested.
  const visibility = summaryVisibility(useBilling(s => s.summaryLoaded), canManage)

  // The Stripe customer balance, printed as one line under the invoices (router#1315). Written
  // only by the summary read, like `cancelAtPeriodEnd`, and cleared with it on every org switch,
  // so a non-null value is always this organization's own and always from a landed read.
  // ⚠ A primitive off the store — a stable snapshot under Zustand v5. `balanceLine` owns the
  // sign: null for zero, for a router that predates the field, and for anything not an integer.
  const balance = balanceLine(useBilling(s => s.customerBalanceCents))

  // ⚠ The deploy order is the router first, so during that window this app is talking to a router
  // with no `billing` tool and the read fails. Say so in one line rather than rendering three
  // empty sections that look like an account with no card and no history.
  if (summaryError) {
    return (
      <>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Payment details and invoices are temporarily unavailable. Everything is still in the Stripe billing portal.
        </Typography>
      </>
    )
  }

  return (
    <>
      {/* Payment */}
      <Divider sx={{ mb: 2 }} />
      {/* ⚠ `flushRight` on the actions in this panel. A text Button's box ends on the container edge
          but its LABEL ends 16px short of it, and the invoice "View" link has no padding, so the
          three actions read as a ragged column without it. See `formAlign.ts`. */}
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          {/* ⚠ A SECTION TITLE, ONE LEVEL UNDER THE PANEL TITLE (app#212). `variant` sets the
              tag as well as the scale, so all three of these were `h6` under an `h6`. */}
          <Typography variant="subtitle2" component="h3" sx={{ color: 'text.secondary' }}>
            Payment
          </Typography>
          {/* ⚠⚠ NOT FETCHED, so nothing below may run (app#216): "no card" would be a claim about
              this organization's money read off a value nobody asked for. A non-Owner is told who
              can see it; an Owner is mid-flight and is told nothing at all, rather than something
              wrong for an instant. */}
          {visibility === 'restricted' && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              The payment method is visible to the organization Owner.
            </Typography>
          )}
          {visibility === 'shown' &&
            (card ? (
              <PaymentMethodLine card={card} />
            ) : (
              // ⚠ Two states share this line and it must not claim to know which: the org may have
              // no method at all, or several with none set as the default, where naming one would
              // be a guess about which gets charged (router#1133).
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No default payment method on file
              </Typography>
            ))}
        </Box>
        {/* ⚠⚠ ABSENT, NOT DISABLED, until the read has landed — the rule this file already
            follows for a Free org. The label is derived from `card`, so a reader whose summary
            never ran got a greyed-out "Add a card" that both claimed there was none and led
            nowhere; the router refuses the portal for them anyway, and the alert above the panel
            already says the Owner is who changes this (app#216). ⚠ 'loading' is gated for the
            SAME reason and not only 'restricted': `card` is null while the read is in flight, so
            an Owner of an org that HAS a card read "Add a card" until it answered. */}
        {visibility === 'shown' && (
          <Button size="small" sx={flushRight} disabled={!canManage || !!busy} onClick={() => onPortal('payment_method_update')}>
            {busy === 'card' ? <CircularProgress size="1rem" /> : card ? 'Update' : 'Add a card'}
          </Button>
        )}
      </Stack>

      {/* Invoices */}
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" component="h3" sx={{ color: 'text.secondary', mb: 1 }}>
        Invoices
      </Typography>
      {/* ⚠⚠ "No invoices yet" IS A STATEMENT ABOUT THE ORGANIZATION, and this reader never asked
          for one. It was shown to an admin of an org with two paid invoices (app#216). */}
      {visibility === 'restricted' && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Invoices are visible to the organization Owner.
        </Typography>
      )}
      {visibility === 'shown' && invoices.length === 0 && (
        // ⚠ A normal state, not a failure: an org that has never been invoiced has nothing here.
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          No invoices yet.
        </Typography>
      )}
      {visibility === 'shown' && invoices.length > 0 && (
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          {invoices.map(inv => (
            <Stack key={inv.id} direction="row" spacing={2} sx={{ alignItems: 'baseline' }}>
              <Typography variant="body2" sx={{ minWidth: 120 }}>
                {invoiceDate(inv.created)}
              </Typography>
              <Typography variant="body2" sx={{ minWidth: 80 }}>
                {formatCents(inv.totalCents)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1 }}>
                {capitalize(inv.status)}
              </Typography>
              {/* The link is Stripe-hosted and short-lived by design — handed to the browser, never
                  stored. `noreferrer` because it carries a one-off invoice token. */}
              {inv.hostedUrl && (
                <Link href={inv.hostedUrl} target="_blank" rel="noreferrer" variant="body2">
                  View
                </Link>
              )}
            </Stack>
          ))}
        </Stack>
      )}
      {hasMoreInvoices && visibility === 'shown' && (
        <Button size="small" disabled={!!busy} onClick={onLoadMore} sx={{ ...flushLeft, mb: 3 }}>
          {busy === 'invoices' ? <CircularProgress size="1rem" /> : 'Load more'}
        </Button>
      )}
      {/* ⚠⚠ MONEY THE INVOICE LIST CANNOT SHOW (router#1315). A plan downgrade on an annual
          subscription left the customer owed $1,800 — measured — and it appeared on NO surface
          the customer has: not the portal's confirm page, not this list, not Usage. Once the
          portal invoices the proration at once (the router half), the negative net lands on the
          Stripe customer balance, and this is the line that names it.
          ⚠ Gated on `visibility` as well as on the value, for the same reason as the invoice
          rows: a stale non-null cannot happen (the switch clears it), but the panel's rule is
          that only a landed read may make a statement about this organization's money. */}
      {visibility === 'shown' && balance && (
        <Typography variant="caption" component="p" sx={{ color: 'text.secondary', mt: 1 }}>
          {balance}
        </Typography>
      )}

      {/* Cancellation.
          ⚠ ABSENT WITHOUT A SUBSCRIPTION (app#265). An organization that has already moved to
          Free has nothing left to cancel; a Cancel button there opens a portal flow that refuses. */}
      {hasSubscription && (
        <>
        <Divider sx={{ mb: 2, mt: 2 }} />
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="subtitle2" component="h3" sx={{ color: 'text.secondary' }}>
              Cancellation
            </Typography>
            {/* ⚠⚠ WHAT IS KEPT, THEN WHAT STOPS — the Free card's own order (app#210). This paragraph
                said only "nothing is deleted", which is where the customer reads it: at the moment
                of the decision, not while shopping. ⚠ The second half is empty until the catalog
                loads, and the reassurance stands alone then — see `freePlanConsequence`.
                ⚠⚠ AND THE FIRST SENTENCE IS A CLAIM ABOUT `cancelAtPeriodEnd`, which only the
                Owner-only summary read ever writes (app#216). For an admin it keeps its initial
                `false`, so "You keep the plan until the end of the period you have paid for" was
                said to an organization that had already cancelled. Until the read lands, only the
                part that is true either way is printed. */}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {visibility === 'shown' ? (cancelAtPeriodEnd ? ALREADY_ENDING : STILL_RUNNING) : KEPT_ON_CANCEL}
              {landsOn && ` ${landsOn}`}
            </Typography>
          </Box>
          {/* ⚠ Once the subscription is already set to end, "Cancel" would be a second cancellation of
              the same thing. The remedy at that point is to keep the plan, and the portal is where
              that happens. ⚠⚠ Both the COLOUR and the LABEL are read off `cancelAtPeriodEnd`, so
              this control is absent until the summary read that writes it has landed — the same
              rule as the payment button above (app#216). */}
          {visibility === 'shown' && (
            <Button
              size="small"
              sx={flushRight}
              color={cancelAtPeriodEnd ? 'primary' : 'error'}
              disabled={!canManage || !!busy}
              onClick={() => onPortal(cancelAtPeriodEnd ? undefined : 'subscription_cancel')}
            >
              {busy === 'cancel' ? <CircularProgress size="1rem" /> : cancelAtPeriodEnd ? 'Keep my plan' : 'Cancel'}
            </Button>
          )}
        </Stack>
        </>
      )}
    </>
  )
}
